// The coach Syllabus Tracker's Category › Subcategory pickers: which categories and subcategories
// they offer (the admin's course categories, and the admin Courses tree from
// utils/courseSubcategoryNodes.js) and what a pick shows. The columns of every pick are the course
// content the admin published there (backend services/syllabusTrackerTopics.js).
import { getSessionColumnHeader } from './syllabusTopics';

// A subcategory with none of the fixed topics is tracked as its own topic (backend
// models/syllabusEntry.js).
export const SUBCATEGORY_TOPIC_PREFIX = 'subcategory:';
// So is a category with no subcategories (Life Skills, a category the admin added).
export const CATEGORY_TOPIC_PREFIX = 'category:';

// "Balgopal Stories" -> "Balgopal Story", as Add New Content names a subcategory's items.
const singular = (label) => label.trim().replace(/ies$/i, 'y').replace(/([^s])s$/i, '$1');
const nodeKeys = (node) => [node.key, ...node.children.flatMap(nodeKeys)];

/**
 * What the tracker shows: { id, topic, label, title, prefix, subcategories, subcategory }.
 * - topic: the tracker topic the scores are kept under;
 * - label: the name used in the page ("Saint Story Progress"); title: the Excel sheet's name;
 * - prefix: the default column name ("Action Song" 3);
 * - subcategories: for a subcategory below a topic's own row, it and the rows under it, whose
 *   columns are the only ones shown (null = the whole topic); subcategory: the row itself.
 */
export const topicScope = (topic) => ({
  id: topic,
  topic,
  label: topic,
  title: topic,
  prefix: getSessionColumnHeader(topic),
  subcategories: null,
  subcategory: '',
});

// A whole category without subcategories: "category:Life Skills".
export const categoryScope = (name) => {
  const topic = `${CATEGORY_TOPIC_PREFIX}${name}`;
  return { id: topic, topic, label: name, title: name, prefix: singular(name), subcategories: null, subcategory: '' };
};

/**
 * The scope of a pick: `chain` = the picked nodes from the top (empty = the whole category),
 * `categoryTopic` = the category's own topic (Computer Apps -> "Computer apps") and `categoryName`
 * the category's name.
 * - a topic's own row (Action Songs, Saint Stories) or a whole category with a topic: that topic,
 *   exactly as the old Topic dropdown showed it;
 * - a row below it (Action Songs › Vocal Songs): that topic, narrowed to the row's content;
 * - a row without a topic (Stories › Balgopal Stories): its own "subcategory:<key>" topic;
 * - a whole category without a topic (Life Skills): its own "category:<name>" topic.
 */
export function scopeFor(chain, categoryTopic = '', categoryName = '') {
  const node = chain[chain.length - 1];
  if (!node) {
    if (categoryTopic) return topicScope(categoryTopic);
    return categoryName ? categoryScope(categoryName) : null;
  }
  const title = chain.map((n) => n.label).join(' › ');
  if (!node.syllabusTopic) {
    const topic = `${SUBCATEGORY_TOPIC_PREFIX}${node.key}`;
    return { id: topic, topic, label: node.label, title, prefix: singular(node.label), subcategories: null, subcategory: '' };
  }
  const topicAbove = chain.length > 1 ? chain[chain.length - 2].syllabusTopic : categoryTopic;
  if (topicAbove !== node.syllabusTopic) return topicScope(node.syllabusTopic);
  return {
    id: `${node.syllabusTopic}|${node.key}`,
    topic: node.syllabusTopic,
    label: node.label,
    title,
    prefix: getSessionColumnHeader(node.syllabusTopic),
    subcategories: nodeKeys(node),
    subcategory: node.key,
  };
}

/**
 * The picker's categories: [{ name, topic, nodes }]. `categoryNames` = the admin's course categories
 * (Courses page sidebar, in its order), so one the admin adds shows too; until they arrive, the
 * categories buildSubcategoryNodes knows (`tree.categoryNames`).
 */
export function pickerCategories({ nodesFor, topicFor, categoryNames: knownNames }, categoryNames = []) {
  const names = categoryNames.length ? categoryNames : knownNames;
  return names.map((name) => ({ name, topic: topicFor(name), nodes: nodesFor(name) }));
}

// The picked nodes for `path` (node keys from the top), stopping at the first key not found.
export function chainFor(category, path) {
  const chain = [];
  let nodes = category?.nodes || [];
  for (const key of path) {
    const node = nodes.find((n) => n.key === key);
    if (!node) break;
    chain.push(node);
    nodes = node.children;
  }
  return chain;
}

// The pick a category opens on: the whole category when it has a topic, else its first row.
export const defaultPath = (category) => (category && !category.topic && category.nodes[0] ? [category.nodes[0].key] : []);

// Where a fixed topic's own view is ({ category, path }), e.g. "Action Songs" -> English › Action
// Songs; null when no category leads to it.
export function findTopicPick(categories, topic) {
  for (const category of categories) {
    if (category.topic === topic) return { category: category.name, path: [] };
    const walk = (nodes, above, path) => {
      for (const node of nodes) {
        const here = [...path, node.key];
        if (node.syllabusTopic === topic && above !== topic) return here;
        const found = walk(node.children, node.syllabusTopic, here);
        if (found) return found;
      }
      return null;
    };
    const path = walk(category.nodes, category.topic, []);
    if (path) return { category: category.name, path };
  }
  return null;
}

/**
 * The selects to show: [{ nodes, emptyLabel, value }] — the category's rows (none when it has no
 * rows), then the children of each picked row that has some. `emptyLabel` is the "no pick" option:
 * the whole category / row ("All Computer Apps", "All Action Songs"), or the row's own content when
 * it has no topic.
 */
export function pickerLevels(category, chain) {
  if (!category || !category.nodes.length) return [];
  const levels = [{ nodes: category.nodes, emptyLabel: category.topic ? `All ${category.name}` : '', value: chain[0]?.key || '' }];
  chain.forEach((node, index) => {
    if (!node.children.length) return;
    levels.push({
      nodes: node.children,
      emptyLabel: node.syllabusTopic ? `All ${node.label}` : `${node.label} only`,
      value: chain[index + 1]?.key || '',
      parentLabel: node.label,
    });
  });
  return levels;
}

/**
 * A category's topics, each as the scope of its own view (the child popup's tabs): the category's
 * topic, else the rows that start a topic of their own (English: Action Songs, Scenes, Poem), else
 * the whole category. Rows narrowing a topic (Action Songs › Vocal Songs) aren't repeated.
 */
export function categoryScopes(category) {
  if (!category) return [];
  if (category.topic) return [topicScope(category.topic)];
  if (!category.nodes.length) return [categoryScope(category.name)];
  const scopes = [];
  const walk = (nodes, above) =>
    nodes.forEach((node) => {
      const chain = [...above, node];
      const scope = scopeFor(chain, category.topic, category.name);
      if (!scope.subcategories && !scopes.some((s) => s.id === scope.id)) scopes.push(scope);
      walk(node.children, chain);
    });
  walk(category.nodes, []);
  return scopes;
}
